import type { TextFieldProps } from "@mui/material";
import { useCallback,useState } from "react";

import { CanvasFormTextField } from "../forms/styled/CanvasFormTextField.styled";

interface LogicFieldProps {
	label: string;
	value: string;
	onChange: (value: string) => void;
	conditionCount: number;
	disabled?: boolean;
	textFieldProps?: Omit<TextFieldProps, "label" | "value" | "onChange" | "error" | "helperText" | "disabled">;
}

const ALLOWED_KEYWORDS = new Set(["and", "or"]);
const ALLOWED_SYMBOLS = new Set(["(", ")"]);

const tokenize = (input: string): string[] =>
	input
		.trim()
		.split(/\s+/)
		.flatMap((chunk) => {
			const tokens: string[] = [];
			let buffer = "";

			for (const char of chunk) {
				if (ALLOWED_SYMBOLS.has(char)) {
					if (buffer) {
						tokens.push(buffer);
						buffer = "";
					}
					tokens.push(char);
				} else {
					buffer += char;
				}
			}

			if (buffer) tokens.push(buffer);
			return tokens;
		})
		.filter(Boolean);

const validate = (input: string, conditionCount: number): string | null => {
	const trimmed = input.trim();
	if (!trimmed) return null;

	const tokens = tokenize(trimmed);

	for (const token of tokens) {
		if (ALLOWED_SYMBOLS.has(token)) continue;

		if (ALLOWED_KEYWORDS.has(token.toLowerCase())) continue;

		if (/^\d+$/.test(token)) {
			const num = Number(token);
			if (num < 1 || num > conditionCount) {
				return `"${token}" does not match an existing condition (1–${conditionCount})`;
			}
			continue;
		}

		return `Invalid token "${token}". Only condition numbers, AND, OR, and parentheses are allowed.`;
	}

	return null;
};

const LogicField = ({
	label,
	value,
	onChange,
	conditionCount,
	disabled = false,
	textFieldProps,
}: LogicFieldProps) => {
	const [touched, setTouched] = useState(false);

	const error = touched ? validate(value, conditionCount) : null;

	const handleChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			onChange(event.target.value);
		},
		[onChange],
	);

	const handleBlur = useCallback(() => {
		setTouched(true);
	}, []);

	return (
		<CanvasFormTextField
			fullWidth
			{...textFieldProps}
			label={label}
			value={value}
			onChange={handleChange}
			onBlur={handleBlur}
			error={!!error}
			helperText={error}
			disabled={disabled}
			width="100%"
		/>
	);
};

export default LogicField;
